import React from 'react'
import {useStyles} from "./StatisticsSummary.css";
import {InfoRounded} from "@mui/icons-material";
import {Divider} from "@mui/material";
import {useIntl} from "react-intl";

const StatisticsChartInfoSummary = ({metric, aggregation, chartType}) => {
    const classes = useStyles()

    // Internationalization for simple strings
    const intl = useIntl();

    if (!(metric && aggregation && chartType)) {
        return <div/>
    }

    let descriptionPrimary = ''
    let descriptionSecondary = ''
    let descriptionHint = intl.formatMessage({
        id: 'app.statistics.additionalInfo.chartSummary.general.hint',
        defaultMessage: 'Click at the items to display more information.'
    })
    
    let translatedAggregation = intl.formatMessage({id: `app.statistics.filter.form.aggregation.values.${aggregation}`})

    if (metric === 'decisionAccuracy') {
        if (chartType === 'total') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.decisionAccuracy.total.primary',
                defaultMessage: 'The total number of correct and wrong decisions for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.decisionAccuracy.total.secondary',
                defaultMessage: 'It is sorted by the percentage of correct decisions.'
            })
        }

        if (chartType === 'average') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.decisionAccuracy.average.primary',
                defaultMessage: 'The percentage of correct and wrong decisions for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.decisionAccuracy.average.secondary',
                defaultMessage: 'It is sorted by the percentage of correct decisions.'
            })
        }

        if (chartType === 'accumulated') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.decisionAccuracy.accumulated.primary',
                defaultMessage: 'The development of the average correct decision percentage for each {aggregation} over time.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.decisionAccuracy.accumulated.secondary',
                defaultMessage: 'The timeline starts with the first user input in the selected time range. Therefore it might not reflect the currently selected from/to filter criteria.'
            })
        }
    }

    if (metric === 'reactionTime') {
        if (chartType === 'average') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.reactionTime.average.primary',
                defaultMessage: 'The average reaction time for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.reactionTime.average.secondary',
                defaultMessage: 'It is sorted by the average reaction time.'
            })
        }

        if (chartType === 'accumulated') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.reactionTime.accumulated.primary',
                defaultMessage: 'The development of the average reaction time for each {aggregation} over time.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.reactionTime.accumulated.secondary',
                defaultMessage: 'The timeline starts with the first user input in the selected time range. Therefore it might not reflect the currently selected from/to filter criteria.'
            })
        }
    }

    if (metric === 'trainingIntensity') {
        if (chartType === 'total') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.trainingIntensity.total.primary',
                defaultMessage: 'The total training intensity for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.trainingIntensity.total.secondary',
                defaultMessage: 'It is sorted by duration.'
            })
        }

        if (chartType === 'average') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.trainingIntensity.average.primary',
                defaultMessage: 'The average daily training intensity for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.trainingIntensity.average.secondary',
                defaultMessage: 'Calculation is based on the time range chosen in the filter.'
            })
        }

        if (chartType === 'accumulated') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.trainingIntensity.accumulated.primary',
                defaultMessage: 'The development of the average daily training intensity for each {aggregation} over time.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.trainingIntensity.accumulated.secondary',
                defaultMessage: 'The timeline covers the selected time range chosen in the filter.'
            })
        }
    }

    if (metric === 'createdScoredScenes') {
        if (chartType === 'total') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdScoredScenes.total.primary',
                defaultMessage: 'The total number of created scored scenes for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdScoredScenes.total.secondary',
                defaultMessage: 'It is sorted by the number of created scored scenes.'
            })
        }

        if (chartType === 'accumulated') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdScoredScenes.accumulated.primary',
                defaultMessage: 'The development of created scored scenes for each {aggregation} over time.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdScoredScenes.accumulated.secondary',
                defaultMessage: 'The timeline starts with the first created scene in the selected time range. Therefore it might not reflect the currently selected from/to filter criteria.'
            })
        }
    }

    if (metric === 'createdUsers') {
        if (chartType === 'total') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdUsers.total.primary',
                defaultMessage: 'The total number of created users for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdUsers.total.secondary',
                defaultMessage: 'It is sorted by the number of created users.'
            })
        }

        if (chartType === 'accumulated') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdUsers.accumulated.primary',
                defaultMessage: 'The development of created users for each {aggregation} over time.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdUsers.accumulated.secondary',
                defaultMessage: 'The timeline starts with the first created users in the selected time range. Therefore it might not reflect the currently selected from/to filter criteria.'
            })
        }
    }

    if (metric === 'createdChallengedDecisions') {
        if (chartType === 'total') {
            descriptionPrimary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdChallengedDecisions.total.primary',
                defaultMessage: 'The total number of challenged decisions for each {aggregation}.'
            }, {aggregation: translatedAggregation})

            descriptionSecondary = intl.formatMessage({
                id: 'app.statistics.additionalInfo.chartSummary.createdChallengedDecisions.total.secondary',
                defaultMessage: 'It is sorted by the number of challenged decisions.'
            })
        }
    }

    return <div className={classes.descriptionContainer}>
        <div>
            <div>{descriptionPrimary}</div>
            <div>{descriptionSecondary}</div>
        </div>
        <Divider className={classes.divider} orientation="vertical" flexItem/>
        <div className={classes.infoRow}>
            <InfoRounded/>
            <div className={classes.hintText}>{descriptionHint}</div>
        </div>
    </div>

}
export default StatisticsChartInfoSummary
